import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import {
  DSI,
  HANDSHAKE_INTERVAL_MS,
  HANDSHAKE_MAX_TRIES,
  previewOrigin,
  postToFrame,
  asDsiMessage,
  bannerClickFromTarget,
  previewFrameSrc,
  carouselForPath,
  pathnameOf,
  useSiteReveal,
  DEVICE_WIDTHS,
  type BannerClickMessage,
  type PreviewTarget,
  type PreviewDataSource,
  type DsiTarget,
  type DsiSourceInfo,
  type DsiRect,
  type DeviceMode,
} from '../../live-preview';
import { applyVersionArtToSource } from '../../creative-api';
import { LumioMark } from '../LumioMark';
import { Stage, DeviceViewport, Frame, Loading, Hint, HintText, EditHalo, EditPill, EditPillText } from './LivePreview.styles';

export type LivePreviewProps = {
  /** The brand's DEPLOYED site base URL (e.g. https://brand.example). Empty renders nothing. */
  siteUrl: string;
  /** Fired when the owner clicks a banner inside the live site (reported by the dsi bridge). */
  onBannerClick: (message: BannerClickMessage) => void;
  /** What to focus the preview on: a carousel's page + an optional banner. Null = just show the site. */
  target?: PreviewTarget | null;
  /** Fetch the site-faithful preview payload for a carousel (the unpublished draft). Its `source` is pinned
   *  over the live banner data source via `dsi-set-source`. */
  getPreviewSource?: (carousel: string) => Promise<PreviewDataSource | null>;
  /** Fill the host's height (used inside the big preview modal) instead of the fixed inline height. */
  fill?: boolean;
  /** Which device width to render the site at (desktop fills the stage; tablet / mobile are centred). */
  device?: DeviceMode;
  /** Set right after a publish to update the preview INSTANTLY (optimistically): the just-published banner
   *  + its art. We pin the published art at once, then reconcile with the backend. `seq` makes each publish
   *  a distinct trigger. */
  optimisticPublish?: {
    bannerId: string;
    formats?: Record<string, string>;
    imageUrl?: string;
    seq: number;
  } | null;
};

/** Preferred deployed banner-source id keywords per carousel, most-specific first. The deployed sites don't
 *  always match spark's CarouselSource names (a casino page often renders `spark-main-banners`, and a
 *  sports page carries both a casino cross-sell `spark-main-banners` AND the dedicated `spark-sport-banners`),
 *  so we pick the rendered banner source whose id best matches the carousel rather than the first one seen. */
const PREFERRED_KEYWORDS: Record<string, string[]> = {
  homepage: ['main', 'home'],
  casino: ['casino', 'main'],
  sports: ['sport'],
};

/** Choose which discovered banner-source id to pin the carousel's draft over — the first that matches a
 *  preferred keyword, or null if none matches yet (keep waiting for the dedicated source to load). */
const choosePinTarget = (carousel: string | undefined, bannerIds: string[]): string | null => {
  for (const kw of PREFERRED_KEYWORDS[carousel ?? ''] ?? []) {
    const match = bannerIds.find((id) => id.toLowerCase().includes(kw));
    if (match) return match;
  }
  return null;
};

/** A fingerprint of ONE banner's art across a fetched source (its art_bundle.data urls), order-stable, so
 *  the reconciler can tell when the backend has applied a publish to THAT banner. */
const targetArtSig = (source: unknown, bannerId: string): string => {
  const data = (source as { data?: unknown })?.data;
  if (!Array.isArray(data)) return '';
  const rec = (data as Array<Record<string, unknown>>).find((r) => (r?.id ?? r?.banner_id) === bannerId);
  const bundle = (rec?.art_bundle as { data?: Record<string, unknown> } | undefined)?.data;
  if (!bundle) return '';
  return Object.entries(bundle)
    .filter((e): e is [string, string] => typeof e[1] === 'string')
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map((e) => `${e[0]}=${e[1]}`)
    .join(',');
};

/**
 * The live in-website preview over the sanctioned dsi bridge. It embeds the brand's DEPLOYED site, retries
 * the handshake until the bridge answers, discovers the page's banner data source(s), and pins the owner's
 * draft over them. A banner click in the site comes back as `dsi-click` and opens that banner's editor.
 */
export const LivePreview = ({
  siteUrl,
  onBannerClick,
  target = null,
  getPreviewSource,
  fill = false,
  device = 'desktop',
  optimisticPublish = null,
}: LivePreviewProps): React.ReactElement | null => {
  const { t } = useTranslation();

  // The iframe points at the brand's deployed page for the target carousel (+ the cookie-banner-suppress
  // preview params); changing carousel reloads it.
  const frameSrc = React.useMemo(() => previewFrameSrc(siteUrl, target?.carousel), [siteUrl, target?.carousel]);

  const { revealed, onLoad, iframeRef } = useSiteReveal(frameSrc);

  // Latest inputs in refs so the message listener need not re-bind per render.
  const onClickRef = React.useRef(onBannerClick);
  onClickRef.current = onBannerClick;
  const getSourceRef = React.useRef(getPreviewSource);
  getSourceRef.current = getPreviewSource;
  const carouselRef = React.useRef(target?.carousel);
  carouselRef.current = target?.carousel;
  // The carousel currently on screen inside the iframe. Starts as the opened carousel, and follows
  // in-site navigation (home -> sports -> casino) once the bridge re-announces the new URL, so we pin
  // the right draft per page. `lastUrl` distinguishes a real navigation from a set-capture re-ack.
  const activeCarouselRef = React.useRef<string | undefined>(target?.carousel);
  const lastUrlRef = React.useRef<string>('');

  const connectedRef = React.useRef(false);
  const bannerDsIdsRef = React.useRef<string[]>([]);
  // The single deployed banner-source id we pin the carousel's draft over (chosen by keyword match).
  const pinTargetRef = React.useRef<string | null>(null);
  // Set once we've chosen a pin target, so the discovery poll can stop.
  const sourcesFoundRef = React.useRef(false);
  // Bumped on dsi-ready to (re)start banner-source discovery for the freshly-connected page.
  const [readyTick, setReadyTick] = React.useState(0);
  // While a publish awaits backend catch-up, the optimistic art we keep pinning even if the source refetches.
  const pendingOptimisticRef = React.useRef<{ bannerId: string; formats?: Record<string, string>; imageUrl?: string } | null>(null);
  // A stable callback the message listener invokes once the banner sources are known.
  const pinDraftRef = React.useRef<() => void>(() => undefined);

  // Click-to-edit hover cue: the rect (Stage-relative) of the banner currently under the pointer, the
  // node it belongs to (so `dsi-rects` can follow it on scroll), and our current click-capture state.
  const [hoverBox, setHoverBox] = React.useState<DsiRect | null>(null);
  const hoverNodeIdRef = React.useRef<string | null>(null);
  // Click capture is OFF by default so the site navigates normally, and toggled ON only while the pointer
  // is over OUR pinned banner (so clicking it opens the editor instead of following the banner's link).
  const captureOnRef = React.useRef(false);
  const clearHover = React.useCallback((): void => {
    hoverNodeIdRef.current = null;
    setHoverBox(null);
  }, []);

  // Bumped on each iframe load so the handshake effect re-arms for the freshly-loaded page.
  const [loadTick, setLoadTick] = React.useState(0);
  const handleLoad = React.useCallback((): void => {
    connectedRef.current = false;
    bannerDsIdsRef.current = [];
    captureOnRef.current = false;
    activeCarouselRef.current = carouselRef.current;
    lastUrlRef.current = '';
    clearHover();
    onLoad();
    setLoadTick((n) => n + 1);
  }, [onLoad, clearHover]);

  // Pin `content` over the chosen deployed banner source (the one matched to this carousel by keyword).
  const applyPin = React.useCallback(
    (content: unknown): void => {
      const origin = previewOrigin(frameSrc);
      const w = iframeRef.current?.contentWindow;
      const dsId = pinTargetRef.current;
      if (!origin || !w || !dsId) return;
      postToFrame(w, origin, DSI.SET_SOURCE, { dsId, content });
    },
    [frameSrc, iframeRef],
  );

  // Pin the active page's carousel DRAFT (or the optimistic art if a publish is mid-flight) over the live source.
  const pinDraft = React.useCallback(async (): Promise<void> => {
    const carousel = activeCarouselRef.current ?? carouselRef.current;
    if (!carousel || !getSourceRef.current || !pinTargetRef.current) return;
    const res = await getSourceRef.current(carousel);
    if (!res) return;
    const p = pendingOptimisticRef.current;
    const content = p ? applyVersionArtToSource(res.source, p.bannerId, { formats: p.formats, image_url: p.imageUrl }) : res.source;
    applyPin(content);
  }, [applyPin]);
  pinDraftRef.current = () => void pinDraft();

  // Message listener: handshake ack, source discovery, banner clicks, and the hover cue. Trust-checked
  // (origin + window). Only interactions on OUR pinned banner source count as banner interactions —
  // clicks/hovers on other data-source elements (games, cross-sell banners) are left to navigate normally.
  React.useEffect(() => {
    const origin = previewOrigin(frameSrc);
    if (!origin) return undefined;
    // Toggle the bridge's click capture. ON swallows the banner's own navigation so the click opens the
    // editor; OFF (the default) lets every other data-source element navigate. De-duped so hover moves
    // don't spam the frame (each `dsi-set-capture` makes the bridge re-announce `dsi-ready`).
    const applyCapture = (enabled: boolean): void => {
      if (captureOnRef.current === enabled) return;
      captureOnRef.current = enabled;
      const win = iframeRef.current?.contentWindow;
      if (win) postToFrame(win, origin, DSI.SET_CAPTURE, { enabled });
    };
    // A single banner ELEMENT, on ANY banner-typed source on the current page (so hover + click-to-edit
    // keep working after the operator navigates the site, e.g. home -> sports, where a different banner
    // source renders). We deliberately do NOT restrict to the pinned source: the pin only controls which
    // carousel's DRAFT is shown; editing resolves each banner by its own id in the pool. The bridge
    // reports the whole-carousel rect only when it resolves neither an item nor an in-slide field node
    // (both null -> measured = the container), so gating on `item != null` shows the cue on exactly the
    // banners that can be edited and never on the carousel as a whole. Object-presence, NOT `t.item?.index`
    // (index 0 — the first banner — is falsy).
    const isBannerElement = (t: DsiTarget): boolean => t.container?.typeId === 'banner' && t.item != null;
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const msg = asDsiMessage(event.data);
      if (!msg) return;
      if (msg.type === DSI.READY) {
        const url = typeof msg.payload.url === 'string' ? msg.payload.url : '';
        if (!connectedRef.current) {
          connectedRef.current = true;
          lastUrlRef.current = url;
          activeCarouselRef.current = (url && carouselForPath(pathnameOf(url))) || carouselRef.current;
          // The bridge boots with capture ON; force it OFF so the operator can navigate the site freely.
          captureOnRef.current = true;
          applyCapture(false);
          setReadyTick((n) => n + 1); // kick off banner-source discovery (retried until they populate)
        } else if (url && url !== lastUrlRef.current) {
          // The bridge re-announces with a NEW url on an in-site navigation: re-establish for the new
          // page so we pin its carousel's draft. (A set-capture re-ack carries the SAME url — ignored.)
          lastUrlRef.current = url;
          activeCarouselRef.current = carouselForPath(pathnameOf(url)) || activeCarouselRef.current;
          pinTargetRef.current = null;
          sourcesFoundRef.current = false;
          clearHover();
          captureOnRef.current = true;
          applyCapture(false);
          setReadyTick((n) => n + 1);
        }
      } else if (msg.type === DSI.SOURCES) {
        const sources = (msg.payload.sources as DsiSourceInfo[] | undefined) ?? [];
        const banners = sources.filter((s) => s.typeId === 'banner').map((s) => s.id);
        bannerDsIdsRef.current = banners;
        // Pin only once the banner source matching the ACTIVE page's carousel has loaded (e.g. wait for
        // the dedicated spark-sport-banners on a sports page, not the casino cross-sell that also lives
        // there). If no preferred match appears, the discovery poll falls back to the first banner source.
        const target = choosePinTarget(activeCarouselRef.current, banners);
        if (target) {
          pinTargetRef.current = target;
          sourcesFoundRef.current = true;
          pinDraftRef.current();
        }
      } else if (msg.type === DSI.CLICK) {
        // Only a single banner in our pinned carousel opens the editor; a click on the carousel container
        // or anything else was meant as navigation.
        const t = msg.payload as unknown as DsiTarget;
        if (isBannerElement(t)) onClickRef.current(bannerClickFromTarget(t));
      } else if (msg.type === DSI.HOVER) {
        const t = msg.payload as unknown as DsiTarget;
        if (isBannerElement(t) && t.rect) {
          hoverNodeIdRef.current = t.nodeId;
          setHoverBox(t.rect);
          applyCapture(true);
        } else {
          // Hovering the carousel as a whole, a non-banner element, or a banner from another carousel:
          // drop the cue and restore navigation.
          clearHover();
          applyCapture(false);
        }
      } else if (msg.type === DSI.HOVER_OUT) {
        // The cue goes, but capture stays as it is: dropping it here takes two hops to restore while
        // the site reads it synchronously on click, so a banner clicked right after the pointer
        // re-entered it would navigate away instead of opening its editor. The non-banner hover branch
        // above is what restores navigation.
        const nodeId = typeof msg.payload.nodeId === 'string' ? msg.payload.nodeId : null;
        if (!nodeId || nodeId === hoverNodeIdRef.current) {
          clearHover();
        }
      } else if (msg.type === DSI.RECTS) {
        // Follow the hovered banner as the site scrolls/resizes.
        const rects = msg.payload.rects as Record<string, DsiRect> | undefined;
        const id = hoverNodeIdRef.current;
        if (rects && id && rects[id]) setHoverBox(rects[id]);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameSrc, iframeRef, clearHover]);

  // Handshake: after each load, re-send dsi-init until the bridge answers dsi-ready (it is inert until then).
  React.useEffect(() => {
    if (!loadTick) return undefined;
    const origin = previewOrigin(frameSrc);
    const w = iframeRef.current?.contentWindow;
    if (!origin || !w) return undefined;
    let tries = 0;
    postToFrame(w, origin, DSI.INIT);
    const timer = window.setInterval(() => {
      if (connectedRef.current || ++tries >= HANDSHAKE_MAX_TRIES) {
        window.clearInterval(timer);
        return;
      }
      const win = iframeRef.current?.contentWindow;
      if (win) postToFrame(win, origin, DSI.INIT);
    }, HANDSHAKE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadTick, frameSrc, iframeRef]);

  // Banner-source discovery: after dsi-ready, re-request dsi-list-sources until the page's banner data
  // source(s) have populated the store (they load async, so the first reply is often empty). The message
  // handler pins + sets sourcesFoundRef once banners appear, which stops this poll.
  React.useEffect(() => {
    if (!readyTick) return undefined;
    const origin = previewOrigin(frameSrc);
    const w = iframeRef.current?.contentWindow;
    if (!origin || !w) return undefined;
    sourcesFoundRef.current = false;
    pinTargetRef.current = null;
    let tries = 0;
    postToFrame(w, origin, DSI.LIST_SOURCES);
    const timer = window.setInterval(() => {
      if (sourcesFoundRef.current) {
        window.clearInterval(timer);
        return;
      }
      if (++tries >= 15) {
        window.clearInterval(timer);
        // Fallback: no preferred banner source appeared, so pin over the first banner source we did see.
        if (bannerDsIdsRef.current.length) {
          pinTargetRef.current = bannerDsIdsRef.current[0];
          sourcesFoundRef.current = true;
          pinDraftRef.current();
        }
        return;
      }
      const win = iframeRef.current?.contentWindow;
      if (win) postToFrame(win, origin, DSI.LIST_SOURCES);
    }, 600);
    return () => window.clearInterval(timer);
  }, [readyTick, frameSrc, iframeRef]);

  // INSTANT publish: pin the just-published art at once (optimistic), then reconcile with the backend —
  // re-pin its authoritative art once the target banner's live source has moved off the pre-publish art.
  React.useEffect(() => {
    const seq = optimisticPublish?.seq ?? 0;
    if (!seq || !connectedRef.current) return undefined;
    const { bannerId, formats, imageUrl } = optimisticPublish!;
    const hasArt = Boolean(imageUrl || (formats && Object.keys(formats).length));
    if (!hasArt) return undefined;
    const carousel = activeCarouselRef.current ?? carouselRef.current;
    if (!carousel || !getSourceRef.current) return undefined;
    let cancelled = false;
    let attempts = 0;
    let timer: number | null = null;

    void (async () => {
      const res = await getSourceRef.current!(carousel);
      if (cancelled || !res) return;
      const preSig = targetArtSig(res.source, bannerId);
      // Optimistic: pin the published version's art immediately (kept across re-pins via the ref).
      pendingOptimisticRef.current = { bannerId, formats, imageUrl };
      const optimistic = applyVersionArtToSource(res.source, bannerId, { formats, image_url: imageUrl });
      applyPin(optimistic);
      const shownSig = targetArtSig(optimistic, bannerId);

      const poll = async (): Promise<void> => {
        if (cancelled || !getSourceRef.current) return;
        attempts += 1;
        const latest = await getSourceRef.current(carousel);
        if (cancelled || !latest) return;
        const sig = targetArtSig(latest.source, bannerId);
        if (sig === preSig) {
          if (attempts >= 14) {
            pendingOptimisticRef.current = null; // give up: stop forcing the optimistic art
            return;
          }
          const delay = attempts <= 4 ? 800 : attempts <= 8 ? 1500 : 2500;
          timer = window.setTimeout(() => void poll(), delay);
          return;
        }
        // Backend caught up — it's authoritative now; re-pin its art if it differs from what's shown.
        pendingOptimisticRef.current = null;
        if (sig !== shownSig) applyPin(latest.source);
      };
      void poll();
    })();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [optimisticPublish, applyPin]);

  // Empty base URL: in the modal show the loader; inline render nothing.
  if (!frameSrc) return fill ? <Stage $fill={fill}>{<Loading $hidden={false} />}</Stage> : null;

  return (
    <Stage $fill={fill} $device={device}>
      <DeviceViewport $width={DEVICE_WIDTHS[device]}>
        <Frame
          ref={iframeRef}
          src={frameSrc}
          title={t('Live website preview')}
          onLoad={handleLoad}
          $revealed={revealed}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          loading="lazy"
        />
        <Loading $hidden={revealed} aria-hidden={revealed || undefined} />
        {revealed && hoverBox ? (
          <EditHalo
            style={{ left: hoverBox.x, top: hoverBox.y, width: hoverBox.width, height: hoverBox.height }}
            aria-hidden
          >
            <EditPill>
              <Icon name="edit" size={11} />
              <EditPillText text="Click to edit" />
            </EditPill>
          </EditHalo>
        ) : null}
        {revealed ? (
          <Hint>
            <LumioMark size={13} />
            <HintText text="Click any banner on your site to edit it with Lumio." />
          </Hint>
        ) : null}
      </DeviceViewport>
    </Stage>
  );
};

export default LivePreview;
